/**
 * useDismissOnOutside — close a panel/drawer/dialog on Escape or on a
 * pointer press outside its root element.
 *
 * Attaching the outside-click listener is deferred by one tick
 * (setTimeout 0) so the same mousedown that opened the panel (e.g.
 * clicking a robot marker on the map canvas, which uses its own
 * onPointerDown for hit-testing) doesn't also count as "outside" and
 * immediately close it again — a standard technique for this pattern.
 *
 * Presses inside a portalled modal layer (`[data-nx-modal-layer]`, set
 * by `ConfirmDialog`) are NOT "outside", even though they are outside
 * `ref` in the DOM tree. `ConfirmDialog` renders via
 * `createPortal(..., document.body)`, so a dialog opened FROM a drawer
 * is a React child of that drawer but a DOM sibling of it. Without this
 * opt-out, pressing the dialog's own Confirm button dismissed the
 * drawer on `mousedown`, which unmounted the dialog before the `click`
 * that would have fired `onConfirm` could land — so the confirmed
 * action silently never ran, with no error and no network request.
 *
 * This is not hypothetical: it made EVERY manual door and lift command
 * a no-op from the moment those drawers shipped. `audit_events` (which
 * records denials and failures too, not just successes) contained 78
 * command rows, all `task.*`, and zero `door.*`/`lift.*` — while
 * `TaskDetailDrawer`, then the one drawer using this hook WITHOUT a
 * `ConfirmDialog`, had recorded task commands throughout.
 *
 * That contrast is now historical, not a current property of the code:
 * as of the 2026-09-02 batch `TaskDetailDrawer` has a `ConfirmDialog`
 * too (delete-from-history). The evidence above still stands — it is
 * how the bug was found — but do not read it as "this drawer is the
 * one without a dialog". The opt-out is what keeps that drawer's
 * confirm working, and its test covers exactly that.
 */
import { useEffect, type RefObject } from 'react';

/**
 * `ignore` is for a panel whose TRIGGER lives outside it. Without it,
 * pressing the trigger of an open panel dismisses it on `mousedown`
 * and the following `click` toggles it straight back open, so the
 * button appears dead. `AlertBell` and `FavoritesMenu` both hand-rolled
 * this hook rather than take that argument, and both got the open/closed
 * guard backwards -- their listeners existed only while the panel was
 * CLOSED, so neither dismissed on an outside press or on Escape at all,
 * on any input device. That is why it is a parameter here and not a
 * fourth copy there.
 */
export function useDismissOnOutside(
  ref: RefObject<HTMLElement | null>,
  onDismiss: () => void,
  ignore?: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onDismiss();
    }
    function onMouseDown(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      if (!(e.target instanceof Node)) return;
      if (el.contains(e.target)) return;
      if (ignore?.current?.contains(e.target)) return;
      // A portalled modal layer opened by this panel is logically
      // "inside" it even though it is a DOM sibling — see the module
      // docstring for why treating it as outside was silently fatal.
      if (e.target instanceof Element && e.target.closest('[data-nx-modal-layer]')) return;
      onDismiss();
    }

    window.addEventListener('keydown', onKey);
    const attachTimer = window.setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown);
    }, 0);

    return () => {
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(attachTimer);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [ref, onDismiss, ignore]);
}
